import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { ThemeProvider } from 'styled-components';
import { IconContext } from 'react-icons';
import GlobalStyle from './styles/GlobalStyle';
import theme from './styles/themeConfig';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { PostProvider } from './contexts/PostContext';
import { CommentProvider } from './contexts/CommentContext';
import { LayoutProvider, useLayout } from './contexts/LayoutContext';
import { SiteProvider, useSite } from './contexts/SiteContext';

// 페이지 컴포넌트
import Home from './pages/Home';
import BoardList from './pages/BoardList';
import BoardDetail from './pages/BoardDetail';
import PostDetail from './pages/PostDetail';
import PostCreate from './pages/PostCreate';
import PostEdit from './pages/PostEdit';
import Login from './pages/Login';
import Register from './pages/Register';
import NotFound from './pages/NotFound';
import CustomPage from './pages/CustomPage';
import NotNow from './pages/NotNow';
import CertifiedSitePage from './pages/CertifiedSite';
import LandingPage from './pages/LandingPage';
import MyPage from './pages/MyPage';
import Search from './pages/Search';
import InquiryList from './pages/Inquiry/InquiryList';
import InquiryNew from './pages/Inquiry/InquiryNew';
import InquiryDetail from './pages/Inquiry/InquiryDetail';
// 레이아웃 컴포넌트
import Layout from './components/Layout';
import Ranking from './pages/Ranking';
import Connecting from './pages/Connecting';

// 사이드바 없는 페이지 경로 목록
const noSidebarPaths = ['/login', '/register', '/Q', '/W', '/E', '/R', '/T', '/Y', '/U', '/I', '/O', '/P', '/A', '/S', '/D', '/F', '/G', '/H', '/J', '/K', '/L', '/Z', '/X', '/C', '/V', '/B', '/N', '/M', ];

// 사이드바 상태를 관리하는 래퍼 컴포넌트
const PageWithLayoutControl: React.FC = () => {
  const location = useLocation();
  const { setSidebar } = useLayout();
  const { siteConfig } = useSite();

  useEffect(() => {
    // 현재 경로가 사이드바를 표시하지 않는 경로인지 확인
    const shouldHideSidebar = noSidebarPaths.some(path => location.pathname.startsWith(path));
    setSidebar(!shouldHideSidebar);

    // 페이지 제목과 메타 태그 업데이트
    document.title = `${siteConfig.siteName} | 먹튀 사이트 공유`;

    // description 메타 태그 업데이트
    const metaDescription = document.querySelector('meta[name="description"]');
    if (metaDescription) {
      metaDescription.setAttribute('content', `${siteConfig.siteName} - 먹튀 사이트 정보 제공`);
    }
  }, [location, setSidebar, siteConfig]);

  return null;
};

const AppContainer: React.FC = () => {
  const { isAuthenticated, loading } = useAuth();

  return (
    <CommentProvider>
      <Router>
        <LayoutProvider>
          <PageWithLayoutControl />
          <Routes>
            <Route path="/" element={<Layout />}>
              {loading ? <Route index path="*" element={<Connecting />} /> : <>
                <Route index element={<Home />} />
                <Route path="boards">
                  <Route index element={<BoardList />} />
                  <Route path=":boardId" element={<BoardDetail />} />
                </Route>
                <Route path="posts">
                  <Route path=":postId" element={<PostDetail />} />
                  <Route path="create" element={<PostCreate />} />
                  <Route path=":postId/edit" element={<PostEdit />} />
                </Route>
                <Route path="search" element={<Search />} />
                <Route path="q/:page_url" element={<CustomPage />} />
                <Route path="login" element={<Login />} />
                <Route path="register" element={<Register />} />
                <Route path="attendance" element={<NotNow />} />
                <Route path="exchange" element={<NotNow />} />
                <Route path="ranking" element={<Ranking />} />
                <Route path="certified" element={<CertifiedSitePage />} />
                <Route path="inquiry">
                  <Route index element={<InquiryList />} />
                  <Route path="new" element={<InquiryNew />} />
                  <Route path=":inquiryId" element={<InquiryDetail />} />
                </Route>
                <Route path="mypage/*" element={<MyPage />} />
                <Route path="404" element={<NotFound />} />
                <Route path=":pageId" element={<LandingPage />} />
              </>}
            </Route>
          </Routes>
        </LayoutProvider>
      </Router>
    </CommentProvider>
  );
};

const App: React.FC = () => {
  return (
    <ThemeProvider theme={theme}>
      <GlobalStyle />
      <IconContext.Provider value={{ className: 'react-icons', size: '1em' }}>
        <SiteProvider>
          <AuthProvider>
            <PostProvider>
              <AppContainer />
            </PostProvider>
          </AuthProvider>
        </SiteProvider>
      </IconContext.Provider>
    </ThemeProvider>
  );
};

export default App;
